import React, { useState, useEffect } from 'react';
import { Clock, RefreshCw, CheckCircle2 } from 'lucide-react';
import './QueryStateView.scss';

/**
 * Format relative time (e.g. "just now", "2m ago", "1h ago")
 */
function formatTimeAgo(timestamp) {
  if (!timestamp) return null;
  const elapsedSeconds = Math.floor((Date.now() - timestamp) / 1000);

  if (elapsedSeconds < 10) return 'just now';
  if (elapsedSeconds < 60) return `${elapsedSeconds}s ago`;
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * QueryStaleIndicator Component
 * Displays data freshness status and provides an instant manual refresh button.
 *
 * @param {Object} props
 * @param {boolean} [props.isStale=false] - True if query data is considered stale
 * @param {number} [props.dataUpdatedAt] - Timestamp of last successful fetch
 * @param {Function} [props.onRefresh] - Refetch function
 * @param {boolean} [props.isFetching=false] - If currently refreshing
 * @param {string} [props.className='']
 */
export const QueryStaleIndicator = ({
  isStale = false,
  dataUpdatedAt,
  onRefresh,
  isFetching = false,
  className = '',
}) => {
  const [timeAgo, setTimeAgo] = useState(() => formatTimeAgo(dataUpdatedAt));

  useEffect(() => {
    setTimeAgo(formatTimeAgo(dataUpdatedAt));
    const interval = setInterval(() => {
      setTimeAgo(formatTimeAgo(dataUpdatedAt));
    }, 15000); // update every 15s

    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  if (!dataUpdatedAt) return null;

  return (
    <div
      className={`query-stale-badge ${isStale ? 'is-stale' : 'is-fresh'} ${className}`}
      title={isStale ? 'Data is stale and eligible for background refresh' : 'Data is fresh from server'}
    >
      <div className="query-stale-status">
        {isStale ? (
          <Clock size={13} className="query-stale-icon" />
        ) : (
          <CheckCircle2 size={13} className="query-fresh-icon" />
        )}
        <span className="query-stale-text">
          {isStale ? 'Stale' : 'Live'}
          {timeAgo ? ` • ${timeAgo}` : ''}
        </span>
      </div>

      {onRefresh && (
        <button
          type="button"
          className="query-stale-refresh-btn"
          onClick={onRefresh}
          disabled={isFetching}
          title="Refresh server data now"
          aria-label="Refresh server data"
        >
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
        </button>
      )}
    </div>
  );
};

export default QueryStaleIndicator;
